import React, { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { Icon } from './layout/icons/Icons';
import logo from '../assets/icons/augustsays/logo.svg';
import snoonuLogo from '../assets/logos/snoonu.svg';
import veraiLogo from '../assets/logos/verai.svg';
import beaconLogo from '../assets/logos/beacon.svg';
import ivariLogo from '../assets/logos/ivari.svg';
import expandLogo from '../assets/logos/expand.svg';
import screenshot from '../assets/screenshot2.png';
import VideoCarousel from './VideoCarousel';
import { logger } from '../utils/logger';
import './PreviewHomepage.css';

const PreviewHomepage = () => {
  const { instance } = useMsal();

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@600&family=Work+Sans:wght@400;600&family=PT+Mono&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleClick = (e) => {
    const loginTarget = e.target.closest('[data-login]');
    if (loginTarget) {
      e.preventDefault();
      instance.loginRedirect(loginRequest).catch(() => {});
    }
  };

  const handleLoginRedirect = () => {
      instance.loginRedirect(loginRequest).catch((error) => logger.log(error));
  };

  return (
    <div onClick={handleClick} className="previewHome">
      <main>
        <nav className="nav shell" aria-label="Primary navigation">
          <a className="wordmark" href="#top" aria-label="August Says home">
            <Icon name="augustsays/logo" color="currentColor" height="32" width="61" />
            <span className="wordmark-text">August Says</span>
          </a>
          <div className="nav-links">
            <a href="#problem">Why August</a>
            <a href="#how">How it works</a>
            <a className="nav-cta" href="#pilot">Try August</a>
            <a className="nav-cta" onClick={handleLoginRedirect}>Login</a>
          </div>
        </nav>
        <section className="hero shell" id="top">
          <div className="eyebrow data-bold">
            Organizational signal intelligence
          </div>
          <h1 className="brand-blue">Organizations have more data than ever.<br />
          <em>They have less understanding than ever.</em>
          </h1>
          <p className="hero-copy">August helps organizations continuously understand the people shaping what happens next.</p>
          <p className="hero-support">Through adaptive conversations, August uncovers the human signals behind employee, customer and stakeholder experiences so leaders can act with confidence, not assumptions.</p>
          <div className="hero-actions">
            <a className="button button-dark" href="#pilot" data-login="true">Take August for a spin<Icon height="15px" name="arrows/arrow_outward" /></a>
            <a className="text-link" href="#problem">See the problem differently<Icon height="15px" name="arrows/arrow_downward" /></a>
          </div>
          <div className="signal-field" aria-hidden="true">
            <div className="brand-mark"><img src={logo} alt="" /></div>
            {/* <div className="signal-line line-one"></div>
            <div className="signal-line line-two"></div>
            <div className="signal-line line-three"></div> */}
          </div>
        </section>
        <section className="action shell" id="action">
          <div className="section-number">August in action</div>
          <div className="video-placeholder" role="region" aria-label="August in action sizzle reel">
            <div className="video-copy">
              <span className="video-kicker">Sizzle reel</span>
              <h2>See what changes<br />when August listens.</h2>
            </div>
            <div className="video-coming-soon" aria-hidden="true">
              <span className="coming-soon-pill">Coming soon</span>
            </div>
            <img className="cream" src={logo} alt="" />
          </div>
        </section>
        <section className="statement" id="problem">
          <div className="shell narrow">
            <div className="section-number">01 — The problem</div>
            <h2>Most organizations are listening.<br /><span>They&#x27;re just listening in pieces.</span></h2>
            <p>Every system tells part of the story. None reveals what&#x27;s really happening across the organization or why.</p>
          </div>
          <div className="pieces shell">
            <div className="piece">
              <small>01</small>
              <h3>People</h3>
              <p>Engagement surveys</p>
              <div className="fragment"></div>
            </div>
            <div className="piece">
              <small>02</small>
              <h3>Customers</h3>
              <p>Feedback &amp; support</p>
              <div className="fragment"></div>
            </div>
            <div className="piece">
              <small>03</small>
              <h3>Markets</h3>
              <p>Research &amp; social</p>
              <div className="fragment"></div>
            </div>
            <div className="piece">
              <small>04</small>
              <h3>Operations</h3>
              <p>Performance data</p>
              <div className="fragment"></div>
            </div>
          </div>
          <div className="truth shell narrow">
            <p>Meanwhile, people have learned that giving feedback often changes very little.</p>
            <h3>So they stop sharing.<br />Or tell you only what feels safe.</h3>
          </div>
        </section>
        <section className="understanding shell" id="how">
          <div className="section-number">02 — A different kind of listening</div>
          <div className="understanding-grid">
            <div>
              <h2 className="brand-blue">Understand what people actually want to tell you.</h2>
              <p>Every August conversation begins with a question your organization needs to understand. From there, every participant follows a different path.</p>
            </div>
            <div className="conversation-card">
              <div className="card-top">
                <span>Live conversation</span>
                <span className="live"><i></i>Listening</span>
              </div>
              <img className="conversation-screenshot" src={screenshot} alt="August conversation screenshot" />
            </div>
          </div>
          <div className="steps">
            <div>
              <b>01</b>
              <h3>August listens</h3>
              <p>Adaptive conversations make space for what wasn&#x27;t anticipated.</p>
            </div>
            <div>
              <b>02</b>
              <h3>Signals emerge</h3>
              <p>Patterns, tension and context surface across experiences.</p>
            </div>
            <div>
              <b>03</b>
              <h3>Leaders understand</h3>
              <p>Teams see what matters, why it matters and where to act.</p>
            </div>
          </div>
        </section>
        <section className="outcome">
          <div className="shell outcome-grid">
            <div className="section-number">03 — The outcome</div>
            <div>
              <h2 className="brand-blue">Not another dashboard.<br /><span>A clearer view of what&#x27;s really happening.</span></h2>
              <p>August turns human experience into organizational understanding without reducing people to a score.</p>
            </div>
          </div>
          <div className="shell quote">
            <p className="brand-blue">The goal isn&#x27;t to collect more feedback. It&#x27;s to notice what your organization can&#x27;t afford to miss.</p>
          </div>
        </section>
        <section className="testimonials shell" id="testimonials">
          <div className="section-number">04 — What people are saying</div>
          <div className="testimonial-heading">
            <h2 className="brand-blue">Built by listening.<br /><span>Improved by listening.</span></h2>
          </div>
          <div className="testimonial-grid">
            <article className="testimonial-card">
              <span className="testimonial-number">01</span>
              <blockquote>“August’s platform is one of the first approaches I’ve seen that meaningfully modernizes Voice of Customer infrastructure for AI-native organizations.”</blockquote>
              <div className="testimonial-footer">
                <p>Snoonu</p>
                <img className="testimonial-logo" src={snoonuLogo} alt="Snoonu logo" />
              </div>
            </article>
            <article className="testimonial-card">
              <span className="testimonial-number">02</span>
              <blockquote>“I would say it shortens the average 6-8 week discovery cycle by 20% or around $5k per client engagement.”</blockquote>
              <div className="testimonial-footer">
                <p>VerAI</p>
                <img className="testimonial-logo" src={veraiLogo} alt="VerAI logo" />
              </div>
            </article>
            <article className="testimonial-card">
              <span className="testimonial-number">03</span>
              <blockquote>“Because the tool successfully captured the sentiment behind the feedback, we were able to build a product roadmap that addressed root issues and made the client’s team feel genuinely heard and understood.”</blockquote>
              <div className="testimonial-footer">
                <p>Beacon Captial Management</p>
                <img className="testimonial-logo" src={beaconLogo} alt="Beacon Capital Management logo" />
              </div>
            </article>
            <article className="testimonial-card">
              <span className="testimonial-number">04</span>
              <blockquote>“Participants described the experience as easy to access, engaging, intuitive, and surprisingly accurate.”</blockquote>
              <div className="testimonial-footer">
                <p>Ivari</p>
                <img className="testimonial-logo" src={ivariLogo} alt="Ivari logo" />
              </div>
            </article>
            <article className="testimonial-card">
              <span className="testimonial-number">05</span>
              <blockquote>“August Says gave us insights we wouldn’t have uncovered through a standard survey.”</blockquote>
              <div className="testimonial-footer">
                <p>Expand with ACE</p>
                <img className="testimonial-logo" src={expandLogo} alt="Expand with ACE logo" />
              </div>
            </article>
          </div>
        </section>
        <section className="pilot shell" id="pilot">
          <div className="pilot-intro">
            <div className="eyebrow">
    The August pilot
            </div>
            <h2>Take August for a spin.<br /><em>Then tell us what you think.</em></h2>
            <p>This isn&#x27;t a heavy implementation or a long-term commitment. We&#x27;re inviting a small group of people to experience August, explore what it can uncover, and help us make it better.</p>
          </div>
          <div className="pilot-path" aria-label="How the August pilot works">
            <div>
              <b>01</b>
              <h3>Bring a real question</h3>
              <p>Choose something about your people, customers or stakeholders that you genuinely want to understand.</p>
            </div>
            <div>
              <b>02</b>
              <h3>Try August</h3>
              <p>Take the experience for a spin and see how an adaptive conversation goes beyond a predetermined survey.</p>
            </div>
            <div>
              <b>03</b>
              <h3>Give us your feedback</h3>
              <p>Tell us what felt useful, what felt unclear and what would make August more valuable to you.</p>
            </div>
          </div>
          <div className="pilot-ask">
            <div>
              <span>That&#x27;s the ask.</span>
              <strong>Try it. Be candid. Help shape what comes next.</strong>
            </div><a className="button button-dark" href="mailto:hello@augustsays.com?subject=I%27d%20like%20to%20try%20August">Start Here<Icon height="15px" name="arrows/arrow_outward" /></a>
          </div>
        </section>
        <section className="contact shell" id="contact"><img className="contact-mark cream" src={logo} alt="" />
          <div className="eyebrow light">
    Now inviting pilot participants
          </div>
          <h2>Curious enough<br />to <em>take a spin?</em></h2>
          <a className="button button-light" href="mailto:hello@augustsays.com?subject=I%27d%20like%20to%20try%20August">Try August Now<Icon height="15px" name="arrows/arrow_outward" /></a>
        </section>
        <footer>
          <div className="shell footer-inner">
            <a className="wordmark footer-mark" href="#top" aria-label="August Says home">
              <Icon name="augustsays/logo" color="currentColor" height="32" width="61" />
              <span className="wordmark-text">August Says</span>
            </a>
            <p>Organizational signal intelligence.</p>
            <p>© 2026 August Says</p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default PreviewHomepage;
